import { useEffect, useId, useRef, useState } from "react";
import {
  InfoIcon,
  SealCheckIcon,
  UserCirclePlusIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import type { VenueDataSourceInput } from "common/utils";
import { resolveVenueDataSource } from "common/utils";
import { formatDateShort } from "common/utils/general";
import { cn } from "@/lib/utils";

// The resolution rules live in common/utils/venueDataSource so the app, the
// widget and the business dashboard share them. Re-exported here because this
// module is where web components already ask about badge state.
export type { VenueDataSourceInput };
export {
  hasVenueDataSourceBadge,
  isVenueSociabilityVerified,
} from "common/utils";

export function VenueDataSourceBadge({
  venue,
  className = "flex items-center gap-1",
  iconClassName = "w-5 h-5 shrink-0",
  labelClassName = "label-m-semibold text-fg-neutral-subtle whitespace-nowrap",
  withInfo = false,
}: {
  venue: VenueDataSourceInput;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
  /**
   * The venue page's provenance line (Figma 16437:72863): an ⓘ button
   * explains what the label means. Cards keep the compact badge.
   */
  withInfo?: boolean;
}) {
  const i18n = useI18N();
  const dataSource = resolveVenueDataSource(venue);
  if (dataSource === "none") return null;

  // Same team/user badge as VenueCard's "Variant indicators" — replaces the
  // old businessVerified text everywhere on the venue profile page.
  // Phosphor glyphs tinted with theme tokens rather than the fixed-colour
  // /images/*.svg, which painted #352FB8 in dark mode too. Colour is per badge,
  // as on native's VenuePanel: team seal = brand, community glyph = the same
  // neutral ink as its label, so a contribution doesn't read as team-verified.
  const BADGE = {
    team: {
      Icon: SealCheckIcon,
      color: "text-fg-brand-subtle",
      label: i18n.get("sociabilityVerified"),
      info: i18n.get("sociabilityVerifiedInfo"),
    },
    user: {
      Icon: UserCirclePlusIcon,
      color: "text-fg-neutral-subtle",
      label: i18n.get("userUpdated"),
      info: i18n.get("communityContributedInfo"),
    },
  } as const;
  const { Icon, color, label, info } = BADGE[dataSource];

  const lastUpdated = formatDateShort(venue.latestReviewDateUpdated || "");

  return (
    <div className={className}>
      <Icon
        weight="fill"
        aria-hidden="true"
        className={cn(iconClassName, color)}
      />
      {withInfo ? (
        <>
          <span className={labelClassName}>{label}</span>
          <InfoTooltip label={label} message={info} />
          {lastUpdated ? (
            <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap">
              {`: ${lastUpdated}`}
            </span>
          ) : null}
        </>
      ) : (
        <span className={labelClassName}>
          {label}
          {lastUpdated ? `: ${lastUpdated}` : ""}
        </span>
      )}
    </div>
  );
}

/**
 * ⓘ button that toggles a dark tooltip under itself (Figma 16437:73066).
 * Click/tap rather than hover so it works on touch; Escape or a click
 * elsewhere closes it. The message is tied to the button with
 * aria-describedby and announced through a live region when it opens.
 */
function InfoTooltip({ label, message }: { label: string; message: string }) {
  const i18n = useI18N();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={i18n.t("dataSourceInfoButton", { label })}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        // 24px target (WCAG 2.5.8) around the 16px glyph.
        className="flex items-center justify-center w-6 h-6 -m-1 rounded-full text-fg-neutral-subtle hover:text-fg-neutral-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
      >
        <InfoIcon size={16} aria-hidden="true" focusable="false" />
      </button>
      <span
        id={tooltipId}
        role="status"
        className={cn(
          "absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2 w-[172px] flex flex-col items-center",
          open ? "" : "hidden",
        )}
      >
        {/* 20 x 10 pointer, then the bubble. */}
        <span
          aria-hidden="true"
          className="w-0 h-0 border-x-[10px] border-x-transparent border-b-[10px] border-b-[color:var(--container-neutral-subtle,#464649)]"
        />
        <span className="w-full rounded bg-[var(--container-neutral-subtle,#464649)] px-4 py-3 text-center label-s-medium text-fg-neutral-onFilled shadow-md">
          {open ? message : ""}
        </span>
      </span>
    </span>
  );
}
